import { MP4WCPlayer } from './MP4WCPlayer'
const now = () => (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()

class AssetDecodeManager {
  constructor() {
    this._entries = new Map()
    this._debug = {
      createdAt: now(),
    }
    if (typeof window !== 'undefined') {
      try {
        window.__vybyAssetDecodeManager = this
      } catch (e) { /* noop */ }
    }
  }

  static get instance() {
    if (!this._instance) this._instance = new AssetDecodeManager()
    return this._instance
  }

  async getOrCreate({ uuid, blob = null, meta = null, decodeAudio, decodeVideo, preferHardware } = {}) {
    if (!uuid) throw new Error('AssetDecodeManager.getOrCreate requires uuid')
    this.registerAsset({ uuid, blob, meta, options: { decodeAudio, decodeVideo, preferHardware } })
    const session = await this.createSession(uuid, { decodeAudio, decodeVideo, preferHardware })
    return this._buildSessionHandle(uuid, session)
  }

  snapshot() {
    return Array.from(this._entries.values()).map((entry) => ({
      uuid: entry.uuid,
      status: entry.status,
      hasBlob: !!entry.blob,
      decodeAudio: !!entry.decodeAudio,
      decodeVideo: !!entry.decodeVideo,
      createdAt: entry.createdAt,
      lastLoadAt: entry.lastLoadAt,
      refCount: entry.refCount,
      sessionCount: entry.sessions?.size || 0,
      meta: { duration: entry.meta?.duration ?? null, codec: entry.meta?.codec ?? null },
    }))
  }

  registerAsset({ uuid, blob = null, meta = null, options = {} }) {
    if (!uuid) return null
    let entry = this._entries.get(uuid)
    if (!entry) {
      entry = {
        uuid,
        blob: null,
        meta: meta ? { ...meta } : {},
        status: 'idle',
        decodeAudio: options.decodeAudio ?? true,
        decodeVideo: options.decodeVideo ?? true,
        preferHardware: options.preferHardware ?? true,
        createdAt: now(),
        lastLoadAt: null,
        refCount: 0,
        errors: [],
        sessions: new Map(),
        nextSessionId: 1,
      }
      this._entries.set(uuid, entry)
    } else {
      if (meta && typeof meta === 'object') {
        entry.meta = { ...entry.meta, ...meta }
      }
      if (options && typeof options === 'object') {
        if (Object.prototype.hasOwnProperty.call(options, 'decodeAudio')) entry.decodeAudio = !!options.decodeAudio
        if (Object.prototype.hasOwnProperty.call(options, 'decodeVideo')) entry.decodeVideo = !!options.decodeVideo
        if (Object.prototype.hasOwnProperty.call(options, 'preferHardware')) entry.preferHardware = !!options.preferHardware
      }
    }
    if (blob && blob !== entry.blob) {
      entry.blob = blob
      entry.status = 'pending'
      entry.lastLoadAt = null
    }
    return entry
  }

  hasAsset(uuid) {
    return this._entries.has(uuid)
  }

  getAsset(uuid) {
    return this._entries.get(uuid) || null
  }

  _buildSessionHandle(uuid, session) {
    return {
      uuid,
      sessionId: session.id,
      player: session.player,
      get meta() {
        const entry = AssetDecodeManager.instance.getAsset(uuid)
        return entry?.meta ? { ...entry.meta } : {}
      },
      get stats() {
        const entry = AssetDecodeManager.instance.getAsset(uuid)
        const assetStatus = entry?.status || 'idle'
        const sessionStatus = session.status || assetStatus
        return {
          status: assetStatus === 'fallback' ? 'fallback' : sessionStatus,
          assetStatus,
          sessionId: session.id,
          decodeAudio: session.decodeAudio,
          decodeVideo: session.decodeVideo,
          lastLoadAt: session.lastLoadAt ?? entry?.lastLoadAt ?? null,
        }
      },
      retain: () => { this.retain(uuid) },
      release: () => { this.release(uuid, session.id) }
    }
  }

  async createSession(uuid, { decodeAudio, decodeVideo, preferHardware } = {}) {
    const entry = this._entries.get(uuid)
    if (!entry) throw new Error(`AssetDecodeManager: unknown asset ${uuid}`)

    const needAudio = decodeAudio ?? entry.decodeAudio
    const needVideo = decodeVideo ?? entry.decodeVideo
    const preferHw = preferHardware ?? entry.preferHardware
    const session = {
      id: `${uuid}:${entry.nextSessionId++}`,
      decodeAudio: needAudio,
      decodeVideo: needVideo,
      preferHardware: preferHw,
      status: entry.status === 'fallback' ? 'fallback' : (entry.blob ? 'pending' : 'idle'),
      createdAt: now(),
      lastLoadAt: null,
      loadPromise: null,
      released: false,
      errors: [],
      player: null,
    }

    const recordSessionError = (type, err, { assetStatus = null } = {}) => {
      session.errors.push({ type, at: now(), err })
      if (type === 'load' || assetStatus === 'error') {
        session.status = 'error'
      }
      entry.errors.push({ type, at: now(), err, sessionId: session.id })
      if (assetStatus) entry.status = assetStatus
    }

    session.player = new MP4WCPlayer({
      decodeAudio: needAudio,
      preferHardware: preferHw,
      onAudioDecoded: () => {},
      onAudioDecodeError: (err) => {
        recordSessionError('audio', err)
      }
    })

    entry.sessions.set(session.id, session)
    this.retain(uuid)

    try {
      await this._ensureSessionLoaded(entry, session)
      return session
    } catch (err) {
      this.release(uuid, session.id)
      throw err
    }
  }

  async _ensureSessionLoaded(entry, session) {
    if (!entry) throw new Error('AssetDecodeManager._ensureSessionLoaded requires entry')
    if (!session) throw new Error('AssetDecodeManager._ensureSessionLoaded requires session')
    if (!entry.blob) return session

    if (!session.loadPromise) {
      session.status = 'loading'
      session.loadPromise = (async () => {
        try {
          await session.player.loadFromBlob(entry.blob)
          session.lastLoadAt = now()
          entry.lastLoadAt = session.lastLoadAt
          if (entry.status !== 'fallback') entry.status = 'ready'
          session.status = entry.status === 'fallback' ? 'fallback' : 'ready'
          // Extraction is deferred to the seek/prefill flow (fixed behavior).
        } catch (err) {
          entry.status = 'error'
          session.status = 'error'
          entry.errors.push({ type: 'load', at: now(), err, sessionId: session.id })
          throw err
        }
      })()
    }

    await session.loadPromise
    return session
  }

  retain(uuid) {
    const entry = this._entries.get(uuid)
    if (!entry) return null
    entry.refCount += 1
    if (entry.disposeTimer) {
      clearTimeout(entry.disposeTimer)
      entry.disposeTimer = null
    }
    return entry
  }

  release(uuid, sessionId = null) {
    const entry = this._entries.get(uuid)
    if (!entry) return
    if (sessionId) {
      const session = entry.sessions.get(sessionId)
      if (session) {
        session.released = true
        entry.sessions.delete(sessionId)
        try { session.player?.close?.() } catch (e) { /* noop */ }
      }
    }
    entry.refCount = Math.max(0, entry.refCount - 1)
    if (entry.refCount === 0) {
      entry.idleAt = now()
      if (!entry.disposeTimer) {
        entry.disposeTimer = setTimeout(() => {
          entry.disposeTimer = null
          if (entry.refCount > 0) return
          this.dispose(uuid, { force: true })
        }, 30000)
      }
    }
  }

  dispose(uuid, { force = false } = {}) {
    const entry = this._entries.get(uuid)
    if (!entry) return
    if (entry.disposeTimer) {
      clearTimeout(entry.disposeTimer)
      entry.disposeTimer = null
    }
    if (!force && entry.refCount > 0) return
    entry.sessions?.forEach((session) => {
      try { session.player?.close?.() } catch (e) { /* noop */ }
    })
    entry.sessions?.clear?.()
    this._entries.delete(uuid)
  }

  clearAll() {
    Array.from(this._entries.keys()).forEach((uuid) => this.dispose(uuid, { force: true }))
  }

  setEntryFallback(uuid, reason = 'fallback') {
    const entry = this._entries.get(uuid)
    if (!entry) return
    entry.status = 'fallback'
    entry.fallbackReason = reason
    entry.sessions?.forEach((session) => {
      session.status = 'fallback'
      try { session.player?.stopExtraction?.() } catch (e) { /* noop */ }
    })
  }
}

export const assetDecodeManager = AssetDecodeManager.instance
export default AssetDecodeManager

